import { Badge } from "@/components/_components/badge";
import { Button } from "@/components/_components/button";
import { Card, CardContent } from "@/components/_components/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/_components/dialog";
import { ArrowLeft, Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { PAGE_TITLES, REGEX_PATTERNS } from "@/constants/constants";
import { UI_MESSAGES } from "@/constants/ui-constants";

import { cn } from "@/lib/utils";
import { toast } from "react-hot-toast";
import { buttonStyles } from "theme/components/button";
import { textareaStyles } from "theme/components/textarea";

// Import custom hooks and components
import { useWebchatState } from "./hooks/useWebchatState";
import { useWebchatData } from "./hooks/useWebchatData";
import { useWebchatMessaging } from "./hooks/useWebchatMessaging";
import { ContactForm } from "./components/ContactForm";
import { getJobRole, getInitialOutreachMessage, downloadChatHistoryCSV } from "./utils/chatUtils";
import { ChatMessage, RecipientItem } from "./types";

const WebchatPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Get URL parameters - only transId and agentType are passed from previous page
  const transId = searchParams.get('transId');
  const agentType = searchParams.get('agentType');

  // Create a unique storage key based on transId
  const storageKey = `webchat_${transId || 'default'}`;

  // Custom hooks for state management
  const {
    contactInfo,
    setContactInfo,
    currentMessage,
    setCurrentMessage,
    messages,
    setMessages,
    isChatStarted,
    setIsChatStarted,
    contactInfoSubmitted,
    setContactInfoSubmitted,
    hasSessionError,
    setHasSessionError,
    pollingControlRef,
    resetChatState
  } = useWebchatState(storageKey);

  const {
    recipients,
    transMasterData,
    isLoadingRecipients,
    isLoadingTransMaster
  } = useWebchatData(transId, agentType);

  // Additional state for UI components
  const [selectedCandidate, setSelectedCandidate] = useState<RecipientItem | null>(null);

  const { isSendingMessage, handleSendMessage } = useWebchatMessaging(
    setMessages,
    contactInfo,
    pollingControlRef,
    setHasSessionError,
    transMasterData,
    selectedCandidate
  );

  // Force fresh contact selection on page load - clear all state
  useEffect(() => {
    // Clear all state when component mounts to force fresh session
    resetChatState();
  }, []); // Empty dependency array means this runs only on mount

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Cleanup effect to stop polling when component unmounts
  useEffect(() => {
    return () => {
      pollingControlRef.current.shouldStop = true;
    };
  }, []);

  const validateContactInfo = () => {
    const { email, phone, subChannel } = contactInfo;

    if (!email && !phone) {
      toast.error(UI_MESSAGES.PHONE_OR_EMAIL_REQUIRED);
      return false;
    }

    if (email && !REGEX_PATTERNS.EMAIL.test(email)) {
      toast.error(UI_MESSAGES.INVALID_EMAIL);
      return false;
    }

    if (phone && !REGEX_PATTERNS.PHONE.test(phone)) {
      toast.error(UI_MESSAGES.INVALID_PHONE);
      return false;
    }

    // For TPMO agent type, subChannel is required when phone is selected
    if (agentType === 'TPMO' && phone && !subChannel) {
      toast.error("Sub Channel is required for TPMO agent type");
      return false;
    }

    return true;
  };


  const handleStartChat = () => {
    if (validateContactInfo()) {
      setContactInfoSubmitted(true);
      setIsChatStarted(true);

      // Add initial outreach message if agentType is CANDIDATE_OUTREACH
      if (agentType === 'CANDIDATE_OUTREACH') {
        const contactType = contactInfo.email ? "email" : "phone";
        const initialMessage = getInitialOutreachMessage(transMasterData?.processDetails || null, contactType);

        if (initialMessage) {
          const welcomeMessage: ChatMessage = {
            id: Date.now().toString(),
            text: initialMessage,
            isUser: false,
            timestamp: new Date(),
          };
          setMessages([welcomeMessage]);
        }
      }
    }
  };



  const handleSendMessageWrapper = async () => {
    await handleSendMessage(currentMessage, setCurrentMessage, agentType);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (contactInfoSubmitted) {
        handleSendMessageWrapper();
      } else {
        handleStartChat();
      }
    }
  };

  // Global keyboard shortcut for CSV download
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+D or Cmd+Shift+D to download CSV
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D' && messages.length > 0) {
        e.preventDefault();
        handleDownloadCSV();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [messages.length, contactInfo, transMasterData]);









  const handleDownloadCSV = () => {
    downloadChatHistoryCSV(messages, contactInfo, transMasterData);
  };

  const fetchCandidateDetails = async (_id: number) => {
    // Simplified - just show a message
    toast("Candidate details feature temporarily unavailable");
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  if (!isChatStarted) {
    return (
      <div className="bg-background">
        {/* Header Section */}
        <div className="bg-card border-b border-border">
          <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-foreground">{PAGE_TITLES.WEBCHAT}</h1>
              <p className="mt-2 text-muted-foreground">Connect with our AI-powered chat assistant</p>
            </div>
          </div>
        </div>

        <ContactForm
          contactInfo={contactInfo}
          setContactInfo={setContactInfo}
          recipients={recipients}
          transMasterData={transMasterData}
          isLoadingRecipients={isLoadingRecipients}
          isLoadingTransMaster={isLoadingTransMaster}
          transId={transId}
          agentType={agentType}
          onStartChat={handleStartChat}
          onKeyDown={handleKeyDown}
          setSelectedCandidate={setSelectedCandidate}
        />
      </div>
    );
  }

  return (
    <div className="bg-background">
      {/* Header Section */}
      <div className="bg-card border-b border-border">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <h1 className="text-2xl font-bold text-foreground">{PAGE_TITLES.WEBCHAT}</h1>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  {messages.length} {messages.length === 1 ? 'message' : 'messages'}
                </div>
                {messages.length > 0 && (
                  <>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs text-primary hover:text-primary/80 border-primary/30 hover:border-primary/40 flex items-center gap-1"
                          title="Download chat history as CSV (Ctrl+Shift+D)"
                        >
                          <Download className="h-3 w-3" />
                          Download CSV
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>Download Chat History</DialogTitle>
                          <div className="text-sm text-muted-foreground mt-2">
                            Export your chat conversation as a CSV file for your records.
                          </div>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="bg-muted/50 p-4 rounded-lg space-y-2 border border-border">
                            <div className="text-sm">
                              <span className="font-medium text-foreground">Messages:</span>
                              <span className="ml-2 text-muted-foreground">{messages.length}</span>
                            </div>
                            <div className="text-sm">
                              <span className="font-medium text-foreground">Contact:</span>
                              <span className="ml-2 text-muted-foreground">
                                {contactInfo.email ? (
                                  `📧 ${contactInfo.email}`
                                ) : (
                                  `📱 ${contactInfo.phone}${contactInfo.subChannel ? ` (${contactInfo.subChannel})` : ''}`
                                )}
                              </span>
                            </div>
                            <div className="text-sm">
                              <span className="font-medium text-foreground">Date Range:</span>
                              <span className="ml-2 text-muted-foreground">
                                {messages.length > 0 && (
                                  <>
                                    {messages[0].timestamp.toLocaleDateString()} - {messages[messages.length - 1].timestamp.toLocaleDateString()}
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                          
                          <div className="text-xs text-muted-foreground bg-primary/10 p-3 rounded-lg border border-primary/20">
                            <strong>CSV Format includes:</strong>
                            <ul className="mt-1 space-y-1 list-disc list-inside">
                              <li>Session metadata (contact, role, JobNo, agent type, sub channel)</li>
                              <li>Timestamp (ISO format, date, and time)</li>
                              <li>Sender (User or AI Assistant)</li>
                              <li>Message content and length</li>
                              <li>Message UUID (if available)</li>
                            </ul>
                            <div className="mt-2 pt-2 border-t border-primary/20">
                              <strong>💡 Tip:</strong> Use <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+Shift+D</kbd> for quick download
                            </div>
                          </div>

                          <div className="flex justify-end space-x-2 pt-4">
                            <DialogTrigger asChild>
                              <Button variant="outline">
                                Cancel
                              </Button>
                            </DialogTrigger>
                            <DialogTrigger asChild>
                              <Button
                                onClick={handleDownloadCSV}
                                className="bg-primary hover:bg-primary/90 text-primary-foreground flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Download CSV
                              </Button>
                            </DialogTrigger>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Are you sure you want to clear the chat history? This action cannot be undone.")) {
                          resetChatState();
                          toast.success("Chat history cleared");
                        }
                      }}
                      className="text-xs text-destructive hover:text-destructive/80 border-destructive/30 hover:border-destructive/40"
                    >
                      🗑️ Clear Chat
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Session Information Display */}
            <div className="bg-muted/50 p-4 rounded-lg border border-border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {transMasterData?.externalCd && (
                  <div>
                    <span className="font-medium text-foreground">Job No:</span>
                    <span className="ml-2 text-muted-foreground">{transMasterData.externalCd}</span>
                  </div>
                )}
                {selectedCandidate && (
                  <div>
                    <span className="font-medium text-foreground">Candidate:</span>
                    <button
                      onClick={() => fetchCandidateDetails(selectedCandidate.id)}
                      className="ml-2 text-primary hover:text-primary/80 hover:underline transition-colors font-medium"
                      disabled={false}
                    >
                      {selectedCandidate.fullName}
                    </button>
                  </div>
                )}
                <div className="flex items-center">
                  <span className="font-medium text-foreground">Role:</span>
                  <div className="ml-2">
                    {isLoadingTransMaster ? (
                      <span className="text-muted-foreground">Loading...</span>
                    ) : (
                      <Badge
                        variant="outline"
                        className="bg-primary/10 text-primary border-primary/20 font-medium"
                      >
                        {getJobRole(transMasterData?.addDetails || null)}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-foreground">Contact:</span>
                    <span className="ml-2 text-muted-foreground">
                      {contactInfo.email && (
                        <span className="inline-flex items-center">
                          📧 {contactInfo.email}
                        </span>
                      )}
                      {contactInfo.phone && (
                        <span className="inline-flex items-center">
                          📱 {contactInfo.phone}
                          {contactInfo.subChannel && (
                            <span className="ml-2 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md border border-primary/20">
                              {contactInfo.subChannel}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="h-[600px] shadow-lg border-border">
          <CardContent className="p-0 h-full flex flex-col">
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-6 bg-muted/30">
              <div className="space-y-4 min-h-full">
                {messages.length === 0 ? (
                  <div className="text-center text-muted-foreground mt-20">
                    <div className="max-w-md mx-auto">
                      <h3 className="text-lg font-medium text-foreground mb-2">Welcome to Web Chat</h3>
                      <p className="text-muted-foreground">
                        Start your conversation by typing a message below. Our AI assistant is here to help!
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.isUser ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg break-words ${message.isUser
                            ? "bg-primary text-primary-foreground"
                            : message.text.startsWith("❌")
                              ? "bg-destructive/10 border border-destructive/20 shadow-sm text-destructive"
                              : "bg-card border border-border shadow-sm text-card-foreground"
                            }`}
                        >
                          {message.text.includes('🔄 Click here to refresh') ? (
                            <div className="text-sm">
                              <p className="whitespace-pre-wrap">
                                {message.text.split('\n\n🔄')[0]}
                              </p>
                              <button
                                onClick={handleRefreshPage}
                                className="mt-2 text-primary hover:text-primary/80 underline cursor-pointer"
                              >
                                🔄 Click here to refresh the page and continue chatting.
                              </button>
                            </div>
                          ) : (
                            <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                          )}
                          <p className={`text-xs mt-2 ${message.isUser
                            ? "text-primary-foreground/70"
                            : message.text.startsWith("❌")
                              ? "text-destructive/70"
                              : "text-muted-foreground"
                            }`}>
                            {message.timestamp.toLocaleTimeString()}
                            {message.uuid && (
                              <span className="block text-xs opacity-75 mt-1">
                                UUID: {message.uuid}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
            </div>

            {/* Message Input */}
            <div className={`p-6 border-t border-border ${hasSessionError ? 'bg-destructive/10' : 'bg-card'}`}>
              {hasSessionError && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-destructive text-sm font-medium">
                        ⚠️ Session expired. You can refresh the page or start a new chat.
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => {
                          resetChatState();
                          toast.success("Started fresh chat session");
                        }}
                        size="sm"
                        variant="outline"
                        className="text-primary border-primary/30 hover:bg-primary/10"
                      >
                        🆕 Start Fresh
                      </Button>
                      <Button
                        onClick={handleRefreshPage}
                        size="sm"
                        className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                      >
                        🔄 Refresh Page
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <textarea
                  placeholder={hasSessionError ? "Please refresh the page to continue..." : UI_MESSAGES.ENTER_MESSAGE}
                  value={currentMessage}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCurrentMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={cn(textareaStyles, "flex-1 min-h-[60px] max-h-[120px] resize-none")}
                  disabled={isSendingMessage || hasSessionError}
                />
                <button
                  onClick={handleSendMessageWrapper}
                  disabled={!currentMessage.trim() || isSendingMessage || hasSessionError}
                  className={cn(buttonStyles, "self-end bg-primary hover:bg-primary/90 text-primary-foreground px-6")}
                >
                  {isSendingMessage ? UI_MESSAGES.SENDING : UI_MESSAGES.SEND_MESSAGE}
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


    </div>
  );
};

export default WebchatPage;